import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { CheckCircle2, Eye, History, Scale } from "lucide-react"
import { useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { FichaBanco } from "@/componentes/resumen/FichaBanco"
import { monto } from "@/componentes/tesoreria/dinero"
import { type Adjuntando, AdjuntosNuevos, sumar } from "@/componentes/ui/AdjuntosNuevos"
import { Boton } from "@/componentes/ui/Boton"
import { EstadoCarga, EstadoError } from "@/componentes/ui/Estados"
import { Insignia, type Tono } from "@/componentes/ui/Insignia"
import { Modal } from "@/componentes/ui/Modal"
import { Cabecera, Cuerpo, Fila, Tabla, TablaVacia, Td, Th } from "@/componentes/ui/Tabla"
import { type ArchivoParaVer, VistaPreviaArchivo } from "@/componentes/ui/VistaPreviaArchivo"
import { tienePermiso } from "@/lib/autorizacion"
import { normalizarDecimal, paraElServidor } from "@/lib/decimales"
import { comoFecha, comoFechaHora, hoyLocal } from "@/lib/fechas"
import { usarSesion } from "@/lib/sesion"

/** Lo que el Resumen Macro manda de cada banco. */
export type PartidaBanco = {
  titulo: string
  detalle: string
  moneda: string
  monto: string
  usd: string | null
  clase: string
  suma: boolean
  nota: string | null
  origen: string
  ref_id: number | null
  conciliacion: string | null
  cierre_id: number | null
  corte: string | null
  diferencia: string | null
}

type Cierre = {
  id: number
  origen: string
  cuenta_nombre: string
  moneda: string
  fecha_corte: string
  saldo_sistema: string
  saldo_real: string
  diferencia: string
  motivo: string | null
  estado: string
  responsable: string | null
  creado_por: string | null
  creado_en: string
  validado_por: string | null
  validado_en: string | null
  nombre_original: string
}

const TONO: Record<string, Tono> = {
  SIN_CIERRE: "neutro",
  PENDIENTE_VALIDACION: "info",
  CONCILIADO: "exito",
  CON_DISCREPANCIA: "peligro",
  DESACTUALIZADO: "aviso",
}

const usd = (v: string | null) => (v === null ? "sin tasa" : `$ ${monto(v)}`)
const cuentaDe = (p: PartidaBanco) => ({ origen: p.origen, id: p.ref_id ?? 0, nombre: p.titulo })
type CuentaElegida = ReturnType<typeof cuentaDe>

/**
 * Los bancos del Resumen Macro con su conciliación (RM-2, 23/10). Ver
 * `services/conciliacion.py`.
 *
 * ⚠ **Sólo suma lo CONCILIADO** (criterio 1): el último cierre validado y que cuadra. Lo
 * demás se enseña atenuado con su estado. El cálculo es del servidor.
 *
 * Un clic en la fila abre la consulta del banco (`FichaBanco`, 30/10), sólo con
 * `resumen_macro:movimientos`; los botones de la fila conservan lo suyo, y la foto pública, que
 * no tiene sesión, no abre nada.
 */
export function ListaBancos({
  partidas,
  estados,
  soloLectura = false,
}: {
  partidas: PartidaBanco[]
  estados: Record<string, string>
  /** La foto del enlace público: sin conciliar ni historial (piden sesión). */
  soloLectura?: boolean
}) {
  const usuario = usarSesion((e) => e.usuario)
  const puedeRegistrar = tienePermiso(usuario, "conciliacion:registrar")
  const puedeConsultar = !soloLectura && tienePermiso(usuario, "resumen_macro:movimientos")
  const [conciliando, setConciliando] = useState<CuentaElegida | null>(null)
  const [historial, setHistorial] = useState<CuentaElegida | null>(null)
  const [consultando, setConsultando] = useState<PartidaBanco | null>(null)

  return (
    <>
      <Tabla etiqueta="Bancos y su conciliación">
        <Cabecera>
          <Th>Cuenta</Th>
          <Th derecha>Saldo del sistema</Th>
          <Th derecha>USD</Th>
          <Th>Conciliación</Th>
          <Th>
            <span className="sr-only">Acciones</span>
          </Th>
        </Cabecera>
        <Cuerpo>
          {partidas.length === 0 && <TablaVacia colSpan={5}>No hay cuentas.</TablaVacia>}
          {partidas.map((p) => (
            <Fila
              key={`${p.origen}-${p.ref_id}-${p.titulo}`}
              atenuada={!p.suma}
              className={puedeConsultar && p.ref_id !== null ? "cursor-pointer" : undefined}
              onClick={(e) => {
                if (!puedeConsultar || p.ref_id === null) return
                if (!(e.target as HTMLElement).closest("button")) setConsultando(p)
              }}
            >
              <Td>
                {puedeConsultar && p.ref_id !== null ? (
                  <button
                    type="button"
                    className="foco-cristal text-left hover:underline"
                    aria-label={`Consultar movimientos de ${p.titulo}`}
                    onClick={() => setConsultando(p)}
                  >
                    {p.titulo}
                  </button>
                ) : (
                  p.titulo
                )}
                <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
                  {p.detalle}
                  {p.nota ? ` · ${p.nota}` : ""}
                </span>
              </Td>
              <Td derecha numero>
                {monto(p.monto)} {p.moneda}
              </Td>
              <Td derecha numero>
                {usd(p.usd)}
              </Td>
              <Td>
                {p.conciliacion === null ? (
                  <span className="text-xs" style={{ color: "var(--fg-muted)" }}>
                    —
                  </span>
                ) : (
                  <>
                    <Insignia tono={TONO[p.conciliacion] ?? "neutro"}>
                      {estados[p.conciliacion] ?? p.conciliacion}
                    </Insignia>
                    {p.corte && (
                      <span
                        className="block text-xs tabular-nums"
                        style={{ color: "var(--fg-muted)" }}
                      >
                        corte {comoFecha(p.corte)}
                        {p.diferencia && Number(p.diferencia) !== 0
                          ? ` · dif. ${monto(p.diferencia)}`
                          : ""}
                      </span>
                    )}
                  </>
                )}
              </Td>
              <Td>
                {!soloLectura && p.conciliacion !== null && p.ref_id !== null && (
                  <span className="flex flex-wrap justify-end gap-1">
                    {puedeRegistrar && (
                      <Boton tamano="sm" onClick={() => setConciliando(cuentaDe(p))}>
                        <Scale size={14} aria-hidden={true} /> Conciliar
                      </Boton>
                    )}
                    <Boton
                      tamano="sm"
                      variante="fantasma"
                      onClick={() => setHistorial(cuentaDe(p))}
                      aria-label={`Historial de ${p.titulo}`}
                    >
                      <History size={14} aria-hidden={true} />
                    </Boton>
                  </span>
                )}
              </Td>
            </Fila>
          ))}
        </Cuerpo>
      </Tabla>
      {consultando && consultando.ref_id !== null && (
        <FichaBanco
          origen={consultando.origen}
          cuentaId={consultando.ref_id}
          nombre={consultando.titulo}
          moneda={consultando.moneda}
          saldoLista={consultando.monto}
          nota={consultando.nota}
          onCerrar={() => setConsultando(null)}
        />
      )}
      {conciliando && <DialogoCierre cuenta={conciliando} onCerrar={() => setConciliando(null)} />}
      {historial && (
        <DialogoHistorial
          cuenta={historial}
          estados={estados}
          onCerrar={() => setHistorial(null)}
        />
      )}
    </>
  )
}

function DialogoCierre({ cuenta, onCerrar }: { cuenta: CuentaElegida; onCerrar: () => void }) {
  const clienteQuery = useQueryClient()
  const del2 = cuenta.origen === "2.0"
  const [fecha, setFecha] = useState(hoyLocal())
  const [real, setReal] = useState("")
  const [motivo, setMotivo] = useState("")
  const [responsable, setResponsable] = useState<string | null>(null)
  const [adjuntos, setAdjuntos] = useState<Adjuntando[]>([])
  const [rechazados, setRechazados] = useState<string[]>([])

  const sistema = useQuery({
    queryKey: ["conciliacion-saldo", cuenta.origen, cuenta.id, fecha],
    queryFn: () =>
      api.get<{ nombre: string; moneda: string; saldo: string }>(
        `/api/v1/conciliacion/saldo?origen=${cuenta.origen}&cuenta=${cuenta.id}&fecha=${fecha}`,
      ),
    retry: false,
  })
  const responsables = useQuery({
    queryKey: ["conciliacion-responsables"],
    queryFn: () =>
      api.get<{ id: number; nombre: string; sugerido: boolean }[]>(
        "/api/v1/conciliacion/responsables",
      ),
  })
  const sugerido = del2 ? responsables.data?.find((r) => r.sugerido)?.id : undefined
  const elegido = responsable ?? (sugerido !== undefined ? String(sugerido) : "")

  const realNormal = normalizarDecimal(real)
  const diferencia =
    realNormal !== null && sistema.data
      ? Math.round(Number(realNormal) * 100 - Number(sistema.data.saldo) * 100) / 100
      : null
  const cuadra = diferencia !== null && Math.abs(diferencia) <= 0.01
  const listo =
    realNormal !== null &&
    adjuntos.length > 0 &&
    sistema.isSuccess &&
    (cuadra || motivo.trim().length >= 5)

  const guardar = useMutation({
    mutationFn: () => {
      const cuerpo = new FormData()
      cuerpo.append("origen", cuenta.origen)
      cuerpo.append("cuenta", String(cuenta.id))
      cuerpo.append("fecha_corte", fecha)
      cuerpo.append("saldo_real", paraElServidor(real))
      if (motivo.trim()) cuerpo.append("motivo", motivo.trim())
      if (elegido) cuerpo.append("responsable_id", elegido)
      const soporte = adjuntos[0]
      if (soporte) cuerpo.append("soporte", soporte.archivo)
      return api.post("/api/v1/conciliacion/cierres", cuerpo)
    },
    meta: { exito: "Cierre registrado" },
    onSuccess: () => {
      clienteQuery.invalidateQueries({ queryKey: ["resumen-macro"] })
      clienteQuery.invalidateQueries({ queryKey: ["conciliacion-cierres"] })
      onCerrar()
    },
  })

  return (
    <Modal
      abierto={true}
      titulo={`Conciliar ${cuenta.nombre}`}
      descripcion="El saldo REAL del estado de cuenta a la fecha de corte, con su captura."
      onCerrar={onCerrar}
    >
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault()
          if (listo) guardar.mutate()
        }}
      >
        <label className="block text-sm">
          Fecha de corte
          <input
            type="date"
            className="campo mt-1"
            value={fecha}
            max={hoyLocal()}
            disabled={del2}
            onChange={(e) => setFecha(e.target.value)}
          />
          {del2 && (
            <span className="mt-1 block text-xs" style={{ color: "var(--fg-muted)" }}>
              Una cuenta del 2.0 se cierra a hoy: el 2.0 da su saldo actual.
            </span>
          )}
        </label>
        <p className="text-sm tabular-nums">
          Saldo del sistema:{" "}
          {sistema.isPending ? (
            "…"
          ) : sistema.isError ? (
            <span style={{ color: "var(--destructive)" }}>
              {sistema.error instanceof ErrorApi ? sistema.error.message : "no se pudo leer"}
            </span>
          ) : (
            <strong>
              {monto(sistema.data.saldo)} {sistema.data.moneda}
            </strong>
          )}
        </p>
        <label className="block text-sm">
          Saldo real (del estado de cuenta)
          <input
            className="campo mt-1 tabular-nums"
            inputMode="decimal"
            value={real}
            onChange={(e) => setReal(e.target.value)}
          />
        </label>
        {diferencia !== null && (
          <p
            className="text-sm tabular-nums"
            style={{ color: cuadra ? "var(--success)" : "var(--warning)" }}
          >
            {cuadra ? "Cuadra con el sistema." : `Diferencia: ${monto(diferencia.toFixed(2))}`}
          </p>
        )}
        {!cuadra && (
          <label className="block text-sm">
            Motivo de la diferencia
            <textarea
              className="campo mt-1"
              rows={2}
              maxLength={500}
              value={motivo}
              placeholder="Ej.: ingreso a caja como fondo operativo no registrado en el sistema"
              onChange={(e) => setMotivo(e.target.value)}
            />
          </label>
        )}
        <label className="block text-sm">
          Responsable de validarlo
          <select
            className="campo mt-1"
            value={elegido}
            onChange={(e) => setResponsable(e.target.value)}
          >
            <option value="">Sin asignar</option>
            {(responsables.data ?? []).map((r) => (
              <option key={r.id} value={r.id}>
                {r.nombre}
              </option>
            ))}
          </select>
        </label>
        <AdjuntosNuevos
          adjuntos={adjuntos}
          rechazados={rechazados}
          multiple={false}
          etiqueta="Captura del estado de cuenta"
          onArchivos={(archivos) => {
            const r = sumar([], archivos.slice(0, 1))
            setAdjuntos(r.adjuntos)
            setRechazados(r.rechazados)
          }}
          onQuitar={() => setAdjuntos([])}
        />
        {guardar.isError && (
          <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
            {guardar.error instanceof ErrorApi ? guardar.error.message : "No se pudo registrar"}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Boton variante="fantasma" onClick={onCerrar}>
            Cancelar
          </Boton>
          <Boton variante="primario" type="submit" disabled={!listo || guardar.isPending}>
            Registrar cierre
          </Boton>
        </div>
      </form>
    </Modal>
  )
}

function DialogoHistorial({
  cuenta,
  estados,
  onCerrar,
}: {
  cuenta: CuentaElegida
  estados: Record<string, string>
  onCerrar: () => void
}) {
  const clienteQuery = useQueryClient()
  const usuario = usarSesion((e) => e.usuario)
  const puedeValidar = tienePermiso(usuario, "conciliacion:validar")
  const [viendo, setViendo] = useState<ArchivoParaVer | null>(null)
  const cierres = useQuery({
    queryKey: ["conciliacion-cierres", cuenta.origen, cuenta.id],
    queryFn: () =>
      api.get<Cierre[]>(`/api/v1/conciliacion/cierres?origen=${cuenta.origen}&cuenta=${cuenta.id}`),
  })
  const validar = useMutation({
    mutationFn: (id: number) => api.post(`/api/v1/conciliacion/cierres/${id}/validar`),
    meta: { exito: "Cierre validado" },
    onSuccess: () => {
      clienteQuery.invalidateQueries({ queryKey: ["conciliacion-cierres"] })
      clienteQuery.invalidateQueries({ queryKey: ["resumen-macro"] })
    },
  })

  return (
    <Modal
      abierto={true}
      titulo={`Cierres de ${cuenta.nombre}`}
      descripcion="Cada cierre se corrige registrando otro; no se edita."
      onCerrar={onCerrar}
      ancho="lg"
    >
      {cierres.isPending && <EstadoCarga filas={3} />}
      {cierres.isError && <EstadoError titulo="No se pudo leer" error={cierres.error} />}
      {cierres.data && cierres.data.length === 0 && (
        <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
          Esta cuenta no tiene cierres todavía.
        </p>
      )}
      {validar.isError && (
        <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
          {validar.error instanceof ErrorApi ? validar.error.message : "No se pudo validar"}
        </p>
      )}
      <ul className="space-y-2">
        {(cierres.data ?? []).map((c) => (
          <li key={c.id} className="superficie space-y-1 p-3 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium tabular-nums">Corte {comoFecha(c.fecha_corte)}</span>
              <Insignia tono={TONO[c.estado] ?? "neutro"}>{estados[c.estado] ?? c.estado}</Insignia>
            </div>
            <p className="tabular-nums">
              Real {monto(c.saldo_real)} · sistema {monto(c.saldo_sistema)} {c.moneda}
              {Number(c.diferencia) !== 0 ? ` · diferencia ${monto(c.diferencia)}` : ""}
            </p>
            {c.motivo && <p style={{ color: "var(--fg-muted)" }}>Motivo: {c.motivo}</p>}
            <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
              Registró {c.creado_por ?? "—"} el {comoFechaHora(c.creado_en)}
              {c.responsable ? ` · responsable ${c.responsable}` : ""}
              {c.validado_en
                ? ` · validó ${c.validado_por ?? "—"} el ${comoFechaHora(c.validado_en)}`
                : ""}
            </p>
            <div className="flex flex-wrap gap-2">
              <Boton
                tamano="sm"
                variante="fantasma"
                onClick={() =>
                  setViendo({
                    ruta: `/api/v1/conciliacion/cierres/${c.id}/soporte`,
                    nombre: c.nombre_original,
                  })
                }
              >
                <Eye size={14} aria-hidden={true} /> Captura
              </Boton>
              {puedeValidar && c.validado_en === null && (
                <Boton
                  tamano="sm"
                  variante="primario"
                  disabled={validar.isPending}
                  onClick={() => validar.mutate(c.id)}
                >
                  <CheckCircle2 size={14} aria-hidden={true} /> Validar
                </Boton>
              )}
            </div>
          </li>
        ))}
      </ul>
      <VistaPreviaArchivo archivo={viendo} onCerrar={() => setViendo(null)} />
    </Modal>
  )
}
